import React, { useCallback, useEffect } from 'react'
import { GestureResponderEvent } from 'react-native'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { Box, Divider, DatePicker, InputField, SideFilter, Snackbar, Button } from '@/presentation/components'
import { useAuth, useErrorHandler, useMediaQueries, useTheme } from '@/presentation/hooks'
import { SelectStatusTicketFilter, invalidDateState, isLoadingFinancialState, isOpenFinancialFiltersState, ticketsFiltersState, ticketsState } from '@/presentation/pages/Financial/components'
import { GetTickets, GetTicketsParams, GetTicketSituation } from '@/domain/usecases'
import { DateAdapter } from '@/data/protocols'
import { normalizeTicketFilterDate, TICKET_FILTER_DATE_FORMAT } from './normalize-ticket-filter-date'

type DateParams = {
  dataInicio: string
  dataFim: string
}

export type FiltrosFatura = {
  situacao: GetTicketSituation
  dataInicio: string
  dataFim: string
}

type SideFiltersProps = {
  getTickets: GetTickets
  dateAdapter: DateAdapter
}

export const MARGIN_MODALS = 30

export const SideFilters: React.FC<SideFiltersProps> = ({ getTickets, dateAdapter }) => {
  const { getClient } = useAuth()
  const [isOpen, setOpen] = useRecoilState(isOpenFinancialFiltersState)
  const [filters, setFilters] = useRecoilState(ticketsFiltersState)
  const [isLoading, setLoading] = useRecoilState(isLoadingFinancialState)
  const [invalidDate, setInvalidDate] = useRecoilState(invalidDateState)
  const errorHandler = useErrorHandler()
  const setTickets = useSetRecoilState(ticketsState)
  const { isMobile } = useMediaQueries()
  const theme = useTheme()

  const client = getClient()

  useEffect(() => {
    setFilters({
      situacao: GetTickets.RN.DEFAULT_STATUS,
      dataInicio: returnInitialDateFilter(),
      dataFim: dateAdapter.format(dateAdapter.daysLater(GetTickets.RN.DEFAULT_DAYS_LATER), TICKET_FILTER_DATE_FORMAT)
    })
  }, [])

  const returnInitialDateFilter = (): string => {
    const defaultInitialdate = dateAdapter.daysAgo(GetTickets.RN.DEFAULT_DAYS_AGO)
    const formattedDefaultInitialdate = dateAdapter.format(defaultInitialdate, TICKET_FILTER_DATE_FORMAT)

    if (client.limiteDataRetroativa) {
      const limitDate = dateAdapter.parse(client.limiteDataRetroativa)
      const isBefore = dateAdapter.isBefore({ startDate: defaultInitialdate, endDate: limitDate })
      return isBefore ? dateAdapter.format(limitDate, TICKET_FILTER_DATE_FORMAT) : formattedDefaultInitialdate
    }

    return formattedDefaultInitialdate
  }

  const handleGetTickets = (event: GestureResponderEvent): void => {
    event.preventDefault()
    setLoading(true)

    const dataInicial = normalizeTicketFilterDate(
      dateAdapter,
      filters.dataInicio,
      dateAdapter.parse(returnInitialDateFilter())
    )
    const dataFinal = normalizeTicketFilterDate(
      dateAdapter,
      filters.dataFim,
      dateAdapter.daysLater(GetTickets.RN.DEFAULT_DAYS_LATER)
    )

    const paramsGetTicket: GetTicketsParams = {
      codigoCliente: client.codCliente,
      situacao: filters.situacao,
      dataInicial,
      dataFinal
    }

    setFilters(currentFilters => ({
      ...currentFilters,
      dataInicio: dataInicial,
      dataFim: dataFinal
    }))

    getTickets
      .get(paramsGetTicket)
      .then(setTickets)
      .catch(errorHandler)
      .finally(() => setLoading(false))

    setOpen(false)
  }

  const handleChangeDate = useCallback((field: keyof DateParams, date: Date): void => {
    if (client.limiteDataRetroativa && field === 'dataInicio') {
      const filterParsedDate = dateAdapter.parse(client.limiteDataRetroativa)
      const isValid = dateAdapter.isBefore({ startDate: date, endDate: filterParsedDate })

      if (isValid) return setInvalidDate(true)

      setInvalidDate(false)
    }

    setFilters(currentFilters => ({
      ...currentFilters,
      [field]: dateAdapter.format(date, TICKET_FILTER_DATE_FORMAT)
    }))
  }, [])

  const handleResetState = (): void => {
    setFilters({
      situacao: GetTickets.RN.DEFAULT_STATUS,
      dataInicio: dateAdapter.format(dateAdapter.daysAgo(GetTickets.RN.DEFAULT_DAYS_AGO), TICKET_FILTER_DATE_FORMAT),
      dataFim: dateAdapter.format(dateAdapter.daysLater(GetTickets.RN.DEFAULT_DAYS_LATER), TICKET_FILTER_DATE_FORMAT)
    })
  }

  const handleClose = (): void => {
    setInvalidDate(false)
    setOpen(false)
  }

  const closeLimitDateAlert = (event: GestureResponderEvent): void => {
    event.preventDefault()
    setInvalidDate(false)
  }

  return (
    <SideFilter.Drawer
      open={isOpen}
      onClose={handleClose}
      loading={isLoading}
      onSubmit={handleGetTickets}
      resetState={handleResetState}
    >
      <Box testID='data-filtro'>
        <Snackbar
          message={`Data inicial não pode ser anterior a ${dateAdapter.format(client.limiteDataRetroativa, 'DD/MM/YYYY')}`}
          testID='notificacao-date-filter-financial'
          action={
            <Button
              variant='text'
              title='FECHAR'
              color={theme.colors.greyLight}
              onPress={closeLimitDateAlert}
              compact
            />
          }
          style={{
            backgroundColor: theme.colors.error,
            width: isMobile ? '100%' : '500px',
            display: invalidDate ? 'flex' : 'none'
          }}
        />
        <SideFilter.Title title='Período de Vencimento' />
        <InputField>
          <Box style={{ flex: 1 }}>
            <DatePicker
              label='De'
              value={filters.dataInicio
                ? dateAdapter.parse(normalizeTicketFilterDate(
                  dateAdapter,
                  filters.dataInicio,
                  dateAdapter.parse(returnInitialDateFilter())
                ))
                : undefined}
              onChange={(date) => handleChangeDate('dataInicio', date ?? dateAdapter.daysAgo(GetTickets.RN.DEFAULT_DAYS_AGO))}
              dateAdapter={dateAdapter}
            />
          </Box>
          <Divider />
          <Box style={{ flex: 1 }}>
            <DatePicker
              label='Até'
              value={filters.dataFim
                ? dateAdapter.parse(normalizeTicketFilterDate(
                  dateAdapter,
                  filters.dataFim,
                  dateAdapter.daysLater(GetTickets.RN.DEFAULT_DAYS_LATER)
                ))
                : undefined}
              onChange={(date) => handleChangeDate('dataFim', date ?? dateAdapter.daysLater(GetTickets.RN.DEFAULT_DAYS_LATER))}
              dateAdapter={dateAdapter}
            />
          </Box>
        </InputField>
      </Box>
      <Box>
        <SideFilter.Title title='Status da Fatura' />
        <SelectStatusTicketFilter />
      </Box>
    </SideFilter.Drawer>
  )
}
